import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useToast } from '../../hooks/useToast';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
import { FileUpload } from '../ui/FileUpload';
import type { useProject } from '../../hooks/useProject';

type ProjectHookReturn = ReturnType<typeof useProject>;

interface CaptureStageProps {
  projectHook: ProjectHookReturn;
}

export function CaptureStage({ projectHook }: CaptureStageProps) {
  const { project, updateProject } = projectHook;
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [storyText, setStoryText] = useState(project?.raw_story || '');
  const [title, setTitle] = useState(project?.title || '');

  useEffect(() => {
    setTitle(project?.title || '');
  }, [project?.id, project?.title]);

  const handleTitleBlur = () => {
    const trimmed = title.trim();
    if (project && trimmed && trimmed !== project.title) {
      updateProject({ title: trimmed });
    }
  };

  const saveFn = useCallback(
    async (value: string) => {
      await updateProject({ raw_story: value });
    },
    [updateProject],
  );

  const { saving, status, saveError } = useAutoSave(storyText, saveFn);
  const [hasSaved, setHasSaved] = useState(false);
  const prevSaving = useRef(false);
  useEffect(() => {
    if (prevSaving.current && !saving) setHasSaved(true);
    prevSaving.current = saving;
  }, [saving]);

  useEffect(() => {
    if (status === 'error') {
      showToast('Auto-save failed — your latest edit may not be saved', 'error');
    }
  }, [status, saveError, showToast]);

  const handleFileUpload = (files: File[]) => {
    const file = files[0];
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      showToast('Please upload a .txt file', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      if (typeof text === 'string') {
        setStoryText(text);
        showToast('Story loaded from file', 'success');
      }
    };
    reader.onerror = () => {
      showToast('Failed to read file', 'error');
    };
    reader.readAsText(file);
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setStoryText((prev) => prev + text);
        showToast('Text pasted from clipboard', 'success');
      } else {
        showToast('Clipboard is empty', 'info');
      }
    } catch {
      showToast('Could not access clipboard. Please paste manually.', 'error');
    }
  };

  const handleContinue = async () => {
    if (!storyText.trim()) {
      showToast('Please enter a story before continuing', 'warning');
      return;
    }
    await updateProject({ raw_story: storyText, stage: 'define' });
  };

  const charCount = storyText.length;
  const wordCount = storyText.trim() ? storyText.trim().split(/\s+/).length : 0;

  return (
    <>
      <header className="stage-head">
        <div className="stage-head__eyebrow">
          <span className="step-of">step 1 of 4</span> · capture
        </div>
        <h1 className="stage-head__title">
          Bring the story <em>in</em>.
        </h1>
        <p className="stage-head__desc">
          Type, paste, or drop a text file — anything you have. Don't worry about
          formatting; we'll help you compose it into spreads in a minute.
        </p>
      </header>

      <div className="capture">
        <div>
          <div className="capture__paper">
            <input
              className="capture__title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleTitleBlur}
              placeholder="Story title..."
            />
            <Textarea
              value={storyText}
              onChange={(e) => setStoryText(e.target.value)}
              placeholder="Once upon a time..."
              variant="narrative"
              className="capture__textarea-field"
            />
            <div className="capture__footnote">
              <span>{wordCount} words</span>
              <span>·</span>
              <span>{charCount} characters</span>
              <div className="capture__footnote-spacer" />
              {saving && <span className="capture__saving">● saving…</span>}
              {status === 'error' && !saving && (
                <span className="capture__save-error">● save failed</span>
              )}
              {hasSaved && !saving && status !== 'error' && (
                <span className="capture__saved">● saved</span>
              )}
            </div>
          </div>
        </div>

        {/* Mobile-only toolbar — visible only when rail is hidden */}
        <div className="capture__mobile-actions">
          <Button variant="secondary" size="sm" onClick={handlePasteFromClipboard}>
            Paste from clipboard
          </Button>
          <FileUpload
            accept=".txt"
            onFiles={handleFileUpload}
            label="Drop a .txt file here or click to upload"
            hint="Plain text only · up to 200 kb"
          />
        </div>

        <div className="capture__rail">
          <div className="rail-card">
            <div className="rail-card__title">Bring in your draft</div>
            <div className="rail-card__desc">From wherever you've been writing.</div>
            <div className="rail-card__actions">
              <Button variant="secondary" size="sm" onClick={handlePasteFromClipboard}>
                Paste from clipboard
              </Button>
              <FileUpload
                accept=".txt"
                onFiles={handleFileUpload}
                label="Drop a .txt file here or click to upload"
                hint="Plain text only · up to 200 kb"
              />
            </div>
          </div>

          <div className="rail-card">
            <div className="rail-card__title">What we'll do next</div>
            <ul className="rail-card__list">
              <li className="rail-card__list-item">Auto-extract characters, places, objects</li>
              <li className="rail-card__list-item">You pick the illustration style</li>
              <li className="rail-card__list-item">Lay the book out as two-page spreads</li>
              <li className="rail-card__list-item">Generate art into an image bank and place it</li>
              <li className="rail-card__list-item">Print-ready PDF</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="stage-foot">
        <div className="stage-foot__group">
          <Button variant="ghost" onClick={() => navigate('/')}>
            Dashboard
          </Button>
        </div>
        <div className="stage-foot__spacer" />
        <div className="stage-foot__group">
          <Button variant="accent" onClick={handleContinue} disabled={!storyText.trim()}>
            Continue to Define
          </Button>
        </div>
      </div>
    </>
  );
}
